import { useId, useMemo, useState, type ReactNode } from 'react';
import { FiArrowDown, FiArrowUp, FiDownload } from 'react-icons/fi';
import type { AnalyticsTable } from '../../services/analytics';
import { Pagination } from '../ui/Pagination';
import { formatInt } from './format';
import { buildExportSheet, downloadXlsx } from './tableExport';
import { formatCell } from './tableFormat';
import {
    distinctValues,
    EMPTY_FILTER,
    filterRows,
    isFilterActive,
    nextSort,
    sortRows,
    type SortState,
    type TableFilterState,
} from './tableFilter';
import { clampPage, DEFAULT_PAGE_SIZE, isLongToken, PAGE_SIZES, pageCount, pageSlice } from './tablePaging';
import styles from './DataTable.module.css';

interface DataTableProps {
    title: ReactNode;
    /** Linha de apoio sob o titulo. */
    subtitle?: string;
    /** Conteudo entre o titulo e os filtros (ex.: limites de dias). */
    header?: ReactNode;
    hint?: ReactNode;
    table: AnalyticsTable | null;
    loading: boolean;
    error: string | null;
    onRetry: () => void;
    timeZone: string;
    /** Rotulos de exibicao por coluna (o back-end manda rotulos sem acento, usados no Excel). */
    labels?: Record<string, string>;
    /** Colunas mostradas, na ordem. Sem isso, usa as colunas que o back-end mandou. */
    columns?: Array<{ key: string; label: string }>;
    /** Texto de exibicao por coluna, para valores que o back-end manda como codigo. */
    formatters?: Record<string, (value: unknown) => string>;
    /** Ordenacao inicial pelo cabecalho. */
    defaultSort?: SortState;
    /** Cartao proprio: titulo e filtros com margem, barra e tabela de ponta a ponta. */
    card?: boolean;
    emptyMessage: string;
    /** Nome do arquivo do "Exportar Excel": a planilha sai igual a tabela (colunas, textos, busca e ordem). */
    exportFilename: string;
}

/**
 * Tabela do painel com busca, filtro por coluna e valor, contagem e exportacao em Excel.
 * O estado dos filtros zera ao trocar `key` (ex.: outra etapa do funil).
 */
export function DataTable({
    title,
    subtitle,
    header,
    hint,
    table,
    loading,
    error,
    onRetry,
    timeZone,
    labels = {},
    columns: columnsProp,
    formatters,
    defaultSort,
    card = false,
    emptyMessage,
    exportFilename,
}: DataTableProps) {
    const searchId = useId();
    const pageSizeId = useId();
    const columnId = useId();
    const valueId = useId();
    const [filter, setFilter] = useState<TableFilterState>(EMPTY_FILTER);
    const [exportError, setExportError] = useState<string | null>(null);
    const [sort, setSort] = useState<SortState | null>(defaultSort ?? null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

    // Filtro ou ordenacao novos voltam para a primeira pagina.
    const changeFilter = (next: TableFilterState) => {
        setFilter(next);
        setPage(1);
    };
    const changeSort = (key: string) => {
        setSort(nextSort(sort, key));
        setPage(1);
    };

    const columns = useMemo(
        () =>
            columnsProp ?? (table?.columns ?? []).map((c) => ({ key: c.key, label: labels[c.key] ?? c.label })),
        [columnsProp, table, labels],
    );
    const columnKeys = useMemo(() => columns.map((c) => c.key), [columns]);
    const format = useMemo(
        () => (key: string, v: unknown) => {
            const custom = formatters?.[key];
            return custom ? custom(v) : formatCell(v, timeZone);
        },
        [timeZone, formatters],
    );

    const rows = useMemo(() => table?.rows ?? [], [table]);
    const filtered = useMemo(() => filterRows(rows, columnKeys, filter, format), [rows, columnKeys, filter, format]);
    const visible = useMemo(() => sortRows(filtered, sort, format), [filtered, sort, format]);
    const totalPages = pageCount(visible.length, pageSize);
    const currentPage = clampPage(page, visible.length, pageSize);
    const pageRows = useMemo(() => pageSlice(visible, currentPage, pageSize), [visible, currentPage, pageSize]);
    const valueOptions = useMemo(
        () => (filter.column ? distinctValues(rows, filter.column, format) : []),
        [rows, filter.column, format],
    );

    // Exporta as linhas filtradas e ordenadas como na tela (todas as paginas).
    const handleExport = () => {
        setExportError(null);
        try {
            downloadXlsx(exportFilename, table?.name ?? 'Dados', buildExportSheet(columns, visible, format, timeZone));
        } catch (e) {
            setExportError(e instanceof Error ? e.message : 'Não foi possível exportar.');
        }
    };

    return (
        <div
            className={`${styles.wrapper} ${card ? styles.card : ''} ${loading ? styles.loading : ''}`}
            aria-busy={loading}
        >
            <div className={styles.titleRow}>
                <h4 className={styles.title}>{title}</h4>
                {hint && <span className={styles.hint}>{hint}</span>}
            </div>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
            {header && <div className={styles.header}>{header}</div>}

            <div className={styles.controls}>
                <div className={`${styles.field} ${styles.search}`}>
                    <label htmlFor={searchId} className={styles.label}>
                        Buscar
                    </label>
                    <input
                        id={searchId}
                        type="search"
                        className={styles.input}
                        placeholder="Nome, e-mail ou termo"
                        value={filter.search}
                        onChange={(e) => changeFilter({ ...filter, search: e.target.value })}
                    />
                </div>
                <div className={`${styles.field} ${styles.select}`}>
                    <label htmlFor={columnId} className={styles.label}>
                        Filtrar por
                    </label>
                    <select
                        id={columnId}
                        className={styles.input}
                        value={filter.column}
                        onChange={(e) => changeFilter({ ...filter, column: e.target.value, value: '' })}
                    >
                        <option value="">Qualquer coluna</option>
                        {columns.map((c) => (
                            <option key={c.key} value={c.key}>
                                {c.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className={`${styles.field} ${styles.select}`}>
                    <label htmlFor={valueId} className={styles.label}>
                        Valor
                    </label>
                    <select
                        id={valueId}
                        className={styles.input}
                        value={filter.value}
                        disabled={!filter.column}
                        onChange={(e) => changeFilter({ ...filter, value: e.target.value })}
                    >
                        <option value="">Todos os valores</option>
                        {valueOptions.map((v) => (
                            <option key={v} value={v}>
                                {v}
                            </option>
                        ))}
                    </select>
                </div>
                <button
                    type="button"
                    className={styles.clear}
                    disabled={!isFilterActive(filter)}
                    onClick={() => changeFilter(EMPTY_FILTER)}
                >
                    Limpar
                </button>

                <div className={styles.side}>
                    {table && (
                        <span className={styles.count} aria-live="polite">
                            {formatInt(visible.length)} de {formatInt(rows.length)} {rows.length === 1 ? 'linha' : 'linhas'}
                        </span>
                    )}
                </div>
            </div>

            {exportError && (
                <p className={styles.exportError} role="alert">
                    {exportError}
                </p>
            )}

            {error && (
                <div className={styles.error} role="alert">
                    <span>{error}</span>
                    <button type="button" className={styles.retry} onClick={onRetry}>
                        Tentar novamente
                    </button>
                </div>
            )}

            {!error && !table && <p className={styles.message}>Carregando...</p>}

            {table && (
                <div className={styles.box}>
                    <div className={styles.bar}>
                        <span className={styles.barText}>
                            {formatInt(visible.length)} {visible.length === 1 ? 'linha' : 'linhas'} · ordenar pelo cabeçalho
                        </span>
                        <button
                            type="button"
                            className={styles.export}
                            onClick={handleExport}
                            disabled={visible.length === 0}
                        >
                            <FiDownload size={14} aria-hidden="true" />
                            Exportar Excel
                        </button>
                    </div>
                    <div className={styles.scroll}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    {columns.map((c) => {
                                        const active = sort?.key === c.key;
                                        return (
                                            <th
                                                key={c.key}
                                                scope="col"
                                                aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                                            >
                                                <button
                                                    type="button"
                                                    className={styles.sortButton}
                                                    onClick={() => changeSort(c.key)}
                                                >
                                                    {c.label}
                                                    {active &&
                                                        (sort.dir === 'asc' ? (
                                                            <FiArrowUp size={12} aria-hidden="true" />
                                                        ) : (
                                                            <FiArrowDown size={12} aria-hidden="true" />
                                                        ))}
                                                </button>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {pageRows.map((r, i) => (
                                    <tr key={typeof r.contaId === 'string' ? r.contaId : i}>
                                        {columns.map((c) => {
                                            const text = format(c.key, r[c.key]);
                                            const cls =
                                                typeof r[c.key] === 'number'
                                                    ? styles.num
                                                    : isLongToken(text)
                                                      ? styles.long
                                                      : undefined;
                                            return (
                                                <td key={c.key} className={cls}>
                                                    {text}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {visible.length === 0 && (
                            <p className={styles.message}>
                                {rows.length === 0 ? emptyMessage : 'Nenhuma linha com esses filtros.'}
                            </p>
                        )}
                    </div>
                    {visible.length > PAGE_SIZES[0] && (
                        <div className={styles.footer}>
                            <div className={styles.pageSize}>
                                <label htmlFor={pageSizeId} className={styles.pageSizeLabel}>
                                    Linhas por página
                                </label>
                                <select
                                    id={pageSizeId}
                                    className={`${styles.input} ${styles.pageSizeSelect}`}
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(Number(e.target.value));
                                        setPage(1);
                                    }}
                                >
                                    {PAGE_SIZES.map((n) => (
                                        <option key={n} value={n}>
                                            {n}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.pager}>
                                {totalPages > 1 ? (
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        totalItems={visible.length}
                                        itemsPerPage={pageSize}
                                        onPageChange={setPage}
                                    />
                                ) : (
                                    <span className={styles.singlePage}>
                                        Mostrando 1-{visible.length} de {visible.length} resultados
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
